/**
 * Centralized conversion / event tracking.
 *
 * Pushes standardized events to `window.dataLayer` so Google Tag Manager
 * (container GTM-K992QJ4) can route them to Google Ads, GA4, Meta Pixel,
 * and any future tag — from a single place.
 *
 * Also fires direct gtag/fbq calls as a fallback in case GTM is blocked
 * or hasn't loaded yet (defensive; harmless when GTM handles it too).
 *
 * All functions are safe to call in any browser/server environment.
 */

const isBrowser = () => typeof window !== 'undefined';

const push = (payload) => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

/**
 * A qualified lead was captured (any quote form or chatbot lead).
 *
 * @param {Object} params
 * @param {string} params.formType     - Which surface captured the lead ("lp_ceramic", "contact", "chat_widget", ...).
 * @param {string} [params.service]    - Requested service (e.g. "Ceramic Coating", "PPF").
 * @param {number} [params.value]      - Estimated lead value in USD (Google Ads scoring hint).
 * @param {string} [params.email]      - Hashed by GTM if enhanced conversions are on.
 * @param {string} [params.phone]      - Hashed by GTM if enhanced conversions are on.
 * @param {string} [params.name]       - Full name; GTM will split & hash.
 */
export const trackLead = ({
  formType,
  service = '',
  value = 950,
  email = '',
  phone = '',
  name = '',
} = {}) => {
  // 1) GTM-first dataLayer push — the canonical event.
  push({
    event: 'generate_lead',
    lead: {
      form_type: formType,
      service,
      currency: 'USD',
      value,
    },
    // Enhanced-conversion user data (GTM must be configured to consume these).
    user_data: {
      email_address: email,
      phone_number: phone,
      full_name: name,
    },
    // Ecommerce block also populated so GA4 counts this as a conversion.
    ecommerce: {
      currency: 'USD',
      value,
      items: service ? [{ item_name: service, item_category: 'Lead' }] : [],
    },
  });

  // 2) Defensive fallbacks — fire even if GTM tags are misconfigured or blocked.
  if (isBrowser() && typeof window.gtag === 'function') {
    // GA4 event
    window.gtag('event', 'generate_lead', {
      event_category: 'engagement',
      event_label: formType,
      value,
      currency: 'USD',
    });
    // Google Ads conversion (label from existing account)
    window.gtag('event', 'conversion', {
      send_to: 'AW-312506211/LflcCJ3tz8YcEOPugZUB',
      value,
      currency: 'USD',
    });
  }
  if (isBrowser() && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', {
      content_name: formType,
      currency: 'USD',
      value,
    });
  }

  // 3) OpenAI Ads Pixel (oaiq) — pixel: NHYEkEiZrRz1CnnxQzZag7
  //    Every form submission is a `lead` conversion for ChatGPT Ads.
  //    `amount` is in the pixel's minor units (cents) per OpenAI docs.
  if (isBrowser() && typeof window.oaiq === 'function') {
    try {
      window.oaiq('measure', 'lead', {
        type: formType || 'form',
        amount: Math.round((value || 950) * 100),
        currency: 'USD',
      }, {
        event_id: 'lead_' + Date.now() + '_' + Math.floor(Math.random() * 1e6),
      });
    } catch (_) { /* never break the app for a tracking failure */ }
  }
};

/**
 * A completed Stripe purchase (Shop only).
 *
 * @param {Object} params
 * @param {string} params.orderId
 * @param {number} params.value      - Total in USD.
 * @param {Array}  params.items      - [{ id, name, price, quantity }, ...]
 */
export const trackPurchase = ({ orderId, value, items = [] } = {}) => {
  push({
    event: 'purchase',
    ecommerce: {
      transaction_id: orderId,
      currency: 'USD',
      value,
      items: items.map((it) => ({
        item_id: String(it.id ?? ''),
        item_name: it.name ?? '',
        price: it.price ?? 0,
        quantity: it.quantity ?? 1,
      })),
    },
  });

  if (isBrowser() && typeof window.gtag === 'function') {
    window.gtag('event', 'purchase', {
      transaction_id: orderId,
      value,
      currency: 'USD',
    });
    window.gtag('event', 'conversion', {
      send_to: 'AW-312506211/LflcCJ3tz8YcEOPugZUB',
      value,
      currency: 'USD',
      transaction_id: orderId,
    });
  }
  if (isBrowser() && typeof window.fbq === 'function') {
    window.fbq('track', 'Purchase', {
      currency: 'USD',
      value,
      content_ids: items.map((it) => String(it.id ?? '')),
      content_type: 'product',
    });
  }

  // OpenAI Ads Pixel — completed purchase = order_created
  if (isBrowser() && typeof window.oaiq === 'function') {
    try {
      window.oaiq('measure', 'order_created', {
        type: 'contents',
        amount: Math.round((value || 0) * 100),
        currency: 'USD',
      }, {
        event_id: 'order_' + (orderId || Date.now()),
      });
    } catch (_) { /* never break the app for a tracking failure */ }
  }
};

/**
 * A generic conversion event (phone click, chat open, etc.).
 * Used for softer touchpoints Google Ads can use as micro-conversions.
 */
export const trackEvent = (name, params = {}) => {
  push({ event: name, ...params });
  if (isBrowser() && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
};
